(ns frontend.view.task.webentry-resolution.asset
  (:require
   [clojure.string :as string]
   [common.attribute :as attribute]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.constraints :as constraints]
   [common.schema.field :as field]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.components.drag-layout :as drag-layout]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.state.current-settings :as current-settings]
   [frontend.state.permissions :as f-permissions]
   [frontend.utils :as fu]
   [frontend.view.task.webentry-resolution.ai-components :as ai-components]
   [frontend.view.task.webentry-resolution.components :as components]
   [frontend.view.task.webentry-resolution.events :as events]
   [potpuri.core :refer [fn-> fn->>]]
   [reagent.core :as r]
   [ui.components.alert.view :as alert]
   [ui.components.button.view :as button]
   [ui.dom-globals :as dg]
   [ui.editors.data-input :as data-input]
   [ui.util :as uu]))

(defn info-bar
  [{:keys [message matching-asset attachments matching-asset-attachments] :as signals}]
  (let [message @message
        submitted-attachments @attachments
        asset-name (:asset/name @matching-asset)
        asset-attachments @matching-asset-attachments]
    [:div
     {:class "h-100 p-4 bg-base-regular"}
     (when-not (string/blank? message)
       [components/message-info message])
     (when-not (empty? submitted-attachments)
       [components/attachments-section signals :submitted-attachments
        (loc/tr :submitted-attachments-n [(count submitted-attachments)]) submitted-attachments])
     (when-not (empty? asset-attachments)
       [components/attachments-section signals :matching-asset-attachments
        (loc/tr :asset-attachments-n [asset-name (count asset-attachments)]) asset-attachments])]))

(defn asset-summary-view
  [{:keys [_asset-type _resolved-asset-data _resolved-asset _resolved-asset-attachments]}]
  (let [local-state (r/atom {:hide-info? false})]
    (fn [{:keys [asset-type resolved-asset-data resolved-asset resolved-asset-attachments] :as signals}]
      (let [resolved-asset @resolved-asset
            tenant @session/tenant
            tenant-schema @session/tenant-schema
            asset-id (:db/id resolved-asset)
            resolved-asset-data @resolved-asset-data
            resolve-type (get resolved-asset-data :resolved-status)
            asset-type @asset-type
            asset-type-name (loc/tr asset-type)
            type-lc (->> asset-type-name
                         str
                         string/lower-case)
            asset-name (entity/display-string resolved-asset)
            info-title (case resolve-type
                         :created
                         (loc/tr :resolved-asset-created-info-title [asset-type-name asset-name])
                         :updated
                         (loc/tr :resolved-asset-updated-info-title [asset-type-name])
                         :discarded
                         (loc/tr :resolved-asset-discarded-info-title)
                         nil)
            info-text (case resolve-type
                        :created
                        (loc/tr :resolved-asset-created-info [type-lc asset-name])
                        :updated
                        (loc/tr :resolved-asset-updated-info [asset-name])
                        :discarded
                        (loc/tr :resolved-asset-discarded-info [asset-name])
                        nil)
            schema (events/get-asset-summary-schema signals asset-type)
            attachments @resolved-asset-attachments]
        [:div
         {:class "container mt-4 mx-auto max-w-[1024px]"}
         (when (and (not (-> @local-state
                             :hide-info?))
                    info-title
                    info-text)
           [alert/info
            {}
            [:div
             [:h3
              {:class "font-bold"}
              info-title]
             info-text]])
         [:table
          {:class "mt-4 w-full max-w-full border-collapse overflow-x-auto overflow-y-auto border border-solid border-base-light-border-paler"}
          [:thead
           [:tr
            [:th
             {:class    "py-2 px-3 w-[24%] bg-base-regular"
              :col-span "2"}
             (loc/tr :resolved-asset-info [asset-type-name])]]]
          [:tbody
           (doall
            (for [[attribute field-schema] schema
                  :let [label (attribute/attribute-label tenant-schema attribute)
                        value (fu/get-value tenant tenant-schema resolved-asset attribute)
                        display-value (fu/display-data tenant-schema
                                                       tenant
                                                       attribute
                                                       field-schema
                                                       resolved-asset
                                                       :show-currency?
                                                       true)]
                  :when (not (cu/blank-value? value))]
              ^{:key (str "asset-" asset-id "-" attribute)}
              [:tr
               [:td
                {:class "py-2 px-3 w-[24%] align-middle text-semibold border-b border-solid border-base-light-border-paler"}
                label]
               [:td
                {:class "py-2 px-3 w-[24%] align-middle text-semibold border-b border-solid border-base-light-border-paler"}
                [components/truncated-text display-value 75 false]]]))]]
         (when-not (empty? attachments)
           [components/attachments-section signals :resolved-asset-attachments
            (loc/tr :all-attachments-found [type-lc]) attachments])]))))

(defn asset-resolved-view
  [{:keys [asset-type resolved-asset] :as signals}]
  (let [resolved-asset @resolved-asset
        no-asset? (or (nil? resolved-asset)
                      (:common/disabled? resolved-asset))
        asset-type-name (loc/tr @asset-type)
        type-lc (->> asset-type-name
                     str
                     string/lower-case)]
    (if no-asset?

      [:div
       [:div
        {:class "mt-5 text-center"}
        [:img
         {:class "max-w-32"
          :src   "/img/Company-is-deleted.svg"}]]
       [:div
        {:class "text-center font-bold"}
        (loc/tr :entity-not-found-header [type-lc])]
       [:div
        {:class "m-0 text-sm text-center"}
        (loc/tr :entity-not-found-info [type-lc type-lc])]]
      [asset-summary-view signals])))

(defn asset-creator-input
  [{:keys [state]} submitted-asset attachments schema _mandatory-fields]
  (swap! state assoc
         :asset (select-keys submitted-asset
                             (map first schema))
         :asset-attachments {:selected (->> attachments
                                            (map :db/id)
                                            set)})
  (let [draft-state (r/cursor state [:asset])
        error-state (r/cursor state [:errors])
        live-updates-state (r/cursor state [:live-updates])
        attachments-state (r/cursor state [:asset-attachments])]
    (fn [{:keys [asset-type processing?] :as signals} _submitted-asset attachments schema mandatory-fields]
      (let [asset-type @asset-type
            tenant-schema @session/tenant-schema
            asset-type-name (-> (loc/tr asset-type)
                                str
                                string/lower-case)
            attachments-write-access? (f-permissions/field-write-access?
                                       :common/attachments
                                       asset-type
                                       nil)
            errors @error-state
            mandatory-blanks (->> mandatory-fields
                                  (filter (fn [field]
                                            (cu/blank-value? (get @draft-state field)))))
            button-disabled? (boolean (or @processing?
                                          (seq errors)
                                          (->> mandatory-blanks
                                               not-empty)))
            entity-template-lookup (->> schema
                                        (into {}))
            schema (->> schema
                        (remove (fn-> second :field/data-type (= :data.type/currency-symbol))))]
        [:div
         {:class "border border-solid border-base-light-border-paler rounded"}
         [:div
          {:class "p-3 border-b border-solid border-base-light-border-paler text-semibold"}
          (loc/tr :submitted-information)]
         [:div
          {:class "flex flex-col gap-4 p-3"}
          (doall
           (for [[attribute field-schema] schema
                 :let [value      (get @draft-state attribute)
                       mandatory? (boolean (some #{attribute} mandatory-fields))
                       validate!  (fn [updated-attribute new-value]
                                    (let [field-constraints (when (= attribute updated-attribute)
                                                              (:field/constraints field-schema))
                                          errors (constraints/validate field-constraints
                                                                       nil
                                                                       new-value
                                                                       @draft-state
                                                                       (partial
                                                                        session/unique-check
                                                                        false
                                                                        (partial
                                                                         routing/open-entity))
                                                                       loc/tr)]
                                      (if errors
                                        (swap! error-state assoc updated-attribute errors)
                                        (swap! error-state dissoc updated-attribute))
                                      (if (nil? new-value)
                                        (swap! live-updates-state dissoc updated-attribute)
                                        (swap! live-updates-state assoc
                                               updated-attribute
                                               new-value))
                                      errors))
                       save!      (fn [saved-attribute edited-value]
                                    (swap! draft-state assoc saved-attribute edited-value)
                                    (when-let [sa (:field/secondary-attribute field-schema)]
                                      (when (and sa
                                                 (not= sa saved-attribute)
                                                 (some #{sa} (into [] mandatory-fields))
                                                 (not (get @draft-state sa))
                                                 (= :data.type/currency-value
                                                    (:field/data-type field-schema)))
                                        (when-let [default-currency (:tenant/default-currency
                                                                     @session/tenant)]
                                          (swap! draft-state assoc sa default-currency)))))
                       secondary-args
                       (when-let [secondary-attribute (:field/secondary-attribute field-schema)]
                         (let [sec-templ (get entity-template-lookup secondary-attribute)]
                           (merge
                            sec-templ
                            {:attribute          secondary-attribute
                             :value              (or (get @draft-state secondary-attribute)
                                                     (when (= (:field/data-type sec-templ)
                                                              :data.type/currency-symbol)
                                                       (:tenant/default-currency @session/tenant)))
                             :menu-portal-target (dg/element-by-id "react-select-menu-container")
                             :label              (loc/tr secondary-attribute)
                             :mandatory?         mandatory?})))]]
             ^{:key (str "asset-creator-" attribute)}
             [components/creator-field
              {:attribute      attribute
               :field-schema   field-schema
               :value          value
               :label          (or (->> field-schema
                                         :field/translations
                                         :en)
                                    (loc/tr attribute)
                                    (when-let [fs (registry/get-field-schema tenant-schema
                                                                             attribute)]
                                      (loc/field-name attribute fs)))
               :mandatory?     mandatory?
               :error          (get @error-state attribute)
               :containing-entity @draft-state
               :validate!      validate!
               :save!          save!
               :set-error!     (fn [attr err] (swap! error-state assoc attr err))
               :secondary-args secondary-args}]))
          (when (and attachments-write-access?
                     (seq attachments))
            [:div
             {:class "flex flex-col gap-3 my-4"}
             [:div
              {:class "text-xs text-base-light-text-paler"}
              (loc/tr :asset-updater-select-attachments [asset-type-name])]
             [:div
              [components/attachment-selector attachments-state attachments]]])
          [button/sm-primary-solid
           {:disabled? (boolean button-disabled?)
            :class     "mt-3 self-start"
            :on-click  (fn [_]
                         (events/create-asset! signals draft-state attachments-state))}
           (loc/tr :create-new-entity [asset-type-name])]]]))))

;; editor-types specific to this creator:
(defn editor-type
  [attribute field-schema]
  (let [default-editor (->> field-schema
                            :field/default-visibility
                            :editor)
        data-type (:field/data-type field-schema)]
    (cond
      (= attribute :asset/name)
      :editor.type/text-simple
      (field/tag-field? attribute)
      :editor.type/multi-select
      (and (= default-editor :editor.type/none)
           (some #{data-type} [:data.type/ref-single :data.type/keyword]))
      :editor.type/select
      (and (= default-editor :editor.type/none)
           (some #{data-type} [:data.type/ref-multi :data.type/keyword-multi]))
      :editor.type/multi-select
      :else
      (->> field-schema
           :field/default-visibility
           :editor))))

(defn asset-creator
  [{:keys [attachments]} _asset-type _asset]
  (let []
    (fn [{:keys [state] :as signals} asset-type asset]
      (let [schema (registry/get-schema @session/tenant-schema asset-type)
            mandatory-fields (current-settings/mandatory-fields
                              {:entity-type              asset-type
                               :state                    state
                               :default-mandatory-fields [:asset/name]})
            visible-fields (distinct (concat mandatory-fields (keys asset)))
            in-use-fields (events/in-use-asset-fields signals)
            creator-schema
            (->> visible-fields
                 (filter in-use-fields)
                 (map (fn [f]
                        [f (get schema f)]))
                 (filter (fn [[attribute _field-schema]]
                           (let [allow? (f-permissions/field-write-access?
                                         attribute
                                         asset-type
                                         ;;no pipeline restrictions here:
                                         nil)]
                             allow?)))
                 (map (fn [[attribute field-schema]]
                        [attribute
                         (merge field-schema
                                {:attribute   attribute
                                 :access/type :field.access/write
                                 :editor/type (editor-type attribute field-schema)})]))
                 (remove (fn-> second :editor/type (= :editor.type/none))))]
        [:div
         {:class "p-3"}
         [asset-creator-input signals asset @attachments creator-schema mandatory-fields]]))))

(defn create-asset-view
  [{:keys [submitted-asset asset-type has-ai-suggestions? ai-companies ai-status] :as signals}]
  (let [asset-type @asset-type
        asset @submitted-asset
        has-ai? @has-ai-suggestions?
        ai-company-count (count @ai-companies)]
    [:div
     {:class "max-w-[1024px] mx-auto mt-4"}
     [ai-components/ai-unavailable-banner {:status @ai-status}]
     ;; Show AI suggestions banner if available
     (when has-ai?
       [ai-components/ai-suggestions-banner
        {:company-count ai-company-count
         :on-dismiss nil}])  ;; on-apply-all not needed here since fields are already merged
     [:div
      {:class "border border-solid border-base-light-border-paler rounded"}
      [:div
       {:class "p-3 border-b border-solid border-base-light-border-paler"}
       (loc/tr asset-type)]
      [asset-creator signals asset-type asset]]]))

(defn updater-actions
  [{:keys [_updater-state] :as signals}]
  (let [select-action! (fn [kw]
                         (events/select-update-action! signals kw))]
    ;;set start value for selections (update empty fields with submitted data):
    (events/select-update-action! signals :asset-updater-update-empty)
    (fn [{:keys [updater-state]}]
      (let [selected-action (-> @updater-state
                                :action)
            options (->> [:asset-updater-update-empty :asset-updater-keep-current
                          :asset-updater-replace-all :asset-updater-customize]
                         (map (fn [kw]
                                {:value kw
                                 :label (loc/tr kw)})))]
        [:div
         {:class "pb-4 *:flex *:flex-col *:gap-3 *:text-base-light-text"}
         [data-input/view
          {:editor-type :editor.type/radio
           :value       selected-action
           :options     options
           :on-change   select-action!}]]))))

(defn updater-table
  [{:keys [submitted-asset matching-asset updater-state asset-updater-fields] :as signals}]
  (let [submitted-asset (->> (dissoc @submitted-asset :asset/name)
                             (map (fn [[k v]]
                                    [k
                                     (if (vector? v)
                                       (set v)
                                       v)]))
                             (into {}))
        tenant @session/tenant
        tenant-schema @session/tenant-schema
        matching-asset @matching-asset
        updater-fields @asset-updater-fields
        submitted-selected (-> @updater-state
                               :submitted-selected
                               set)]
    [:div
     ;;TODO if no updater-fields, do/show what?
     [:div
      {:class "bg-complement-light"}
      [:div
       {:class-name "p-2 grid grid-cols-2 py-4 divide-x divide-solid divide-base-complement-border-paler *:px-4 *:text-center font-bold"}
       [:div (loc/tr :submitted-value)]
       [:div (loc/tr :current-value-in-db)]]]
     [:div
      (doall
       (for [k updater-fields
             :let [field-schema (registry/get-field-schema tenant-schema k)
                   field-name (attribute/attribute-label tenant-schema k)
                   submitted-value
                   (fu/display-data tenant-schema tenant k field-schema submitted-asset)
                   current-value
                   (fu/display-data tenant-schema tenant k field-schema matching-asset)
                   submitted? (contains? submitted-selected k)]]
         ^{:key (str "asset-updater-table-field-" k)}
         [:div
          [:div
           {:class "p-3 text-center font-bold"}
           field-name]
          [:div
           {:class "relative px-3"}
           [:div
            {:class-name "px-4 grid grid-cols-2 gap-4"}
            [:div
             [:div
              {:class-name (uu/cm "h-8 py-1 px-2 flex items-center gap-2 border border-solid border-base-light-border-paler rounded"
                                  (when (contains? submitted-selected k)
                                    "bg-base-regular"))
               :on-click   (fn [_]
                             (events/select-submitted-asset-field! signals k))}
              (if submitted?
                [:i.fas.fa-check-circle]
                [:i.fal.fa-circle])
              [components/truncated-text
               submitted-value 50 false]]]
            [:div
             [:div
              {:class-name (fu/cs "h-8 py-1 px-2 flex items-center gap-2 border border-solid border-base-light-border-paler rounded"
                                  (when-not (contains? submitted-selected k)
                                    "bg-base-regular"))
               :on-click   (fn [_]
                             (events/select-current-asset-field! signals k))}
              (if submitted?
                [:i.fal.fa-circle]
                [:i.fas.fa-check-circle])
              [components/truncated-text
               current-value 50 false]]]]]]))]]))

(defn updater-attachments
  [{:keys [updater-state attachments]}]
  (let [attachments-state (r/cursor updater-state [:attachments])
        submitted-attachments @attachments]
    (swap! attachments-state assoc
           :selected
           (->> submitted-attachments
                (map :db/id)
                set))
    (fn [{:keys [attachments asset-type]}]
      (let [submitted-attachments @attachments
            asset-type @asset-type
            attachments-write-access? (f-permissions/field-write-access?
                                       :common/attachments
                                       asset-type
                                       nil)
            asset-type-name (->> asset-type
                                 (loc/tr)
                                 (cu/str-trim-lcase))]
        (when (and attachments-write-access?
                   (seq submitted-attachments))
          [:div
           {:class "flex flex-col gap-2"}
           [:div
            {:class "text-base-light-text-paler"}
            (loc/tr :asset-updater-select-attachments [asset-type-name])]
           [components/attachment-selector attachments-state submitted-attachments]])))))

(defn updater-buttons
  [{:keys [asset-type discard? asset-updater-fields attachments] :as signals}]
  (let [asset-type @asset-type
        discard? @discard?
        updater-fields @asset-updater-fields
        submitted-attachments @attachments
        attachments-write-access? (f-permissions/field-write-access?
                                   :common/attachments
                                   asset-type
                                   nil)
        no-attachments? (or (empty? submitted-attachments)
                            (not attachments-write-access?))
        no-info? (and (empty? updater-fields)
                      no-attachments?)
        type-lc (-> asset-type
                    (loc/tr)
                    (cu/str-trim-lcase))]
    [:div
     {:class "inline-flex gap-2 pt-3 px-3"}
     [button/sm-secondary-hollow
      {:class-name "me-3"
       :on-click   (fn [_]
                     (events/update-asset! signals))}
      (cond
        discard?
        (loc/tr :entity-updater-discard-button)
        no-info?
        (loc/tr :asset-updater-use-subsequent [type-lc])
        :else
        (loc/tr :entity-updater-update-button [type-lc]))]
     [button/sm-primary-solid
      {:class-name "me-3"
       :on-click   (fn [_]
                     (events/open-asset-creator! signals))}
      (loc/tr :create-new-entity [type-lc])]]))

(defn update-asset-view
  [{:keys [asset-type matching-asset asset-updater-fields]
    :as   signals}]
  (let [asset-type @asset-type
        asset-type-name (loc/tr asset-type)
        icon (entity/entity-type-icon asset-type)
        matching-asset @matching-asset
        updater-fields @asset-updater-fields
        type-lc (->> asset-type-name
                     str
                     string/lower-case)]
    [:div
     {:class "container mt-4 mx-auto max-w-[1024px]"}
     [:div
      {:class "border border-solid border-base-light-border-paler rounded"}
      [:div
       {:class "py-3 px-4 border-b border-solid border-base-light-border-paler text-bold text-base-light-text"}
       asset-type-name]

      [:div
       {:class "flex flex-col gap-3 p-4"}
       [alert/warning
        {}
        (loc/tr :asset-match-info [type-lc])]

       [:div
        {:class "border border-solid border-base-light-border-paler rounded"}
        [:div
         {:class "py-3 px-4 flex gap-3 items-center border-b border-solid border-base-light-border-paler text-bold text-base-light-text  rounded"}
         [:i {:class-name (uu/cm "fal" icon)}]
         (entity/display-string matching-asset)]

        [:div
         {:class "flex flex-col gap-3 p-3"}
         (when-not (empty? updater-fields)
           [:div
            [:div
             {:class "font-semibold"}
             (loc/tr :asset-updater-title)]])

         (when-not (empty? updater-fields)
           [:div
            [updater-actions signals]
            [updater-table signals]])

         [updater-attachments signals]
         [updater-buttons signals]]]]]]))

(defn no-asset-info-view
  [type-lc]
  [:div
   {:class "py-5 my-5 text-center"}
   [:img
    {:class "max-w-[320px] mx-auto"
     :src   "/img/no-data-created.svg"}]
   [:p
    {:class "mt-4"}
    [:strong
     {:class "text-semibold"}
     (loc/tr :no-webform-asset [type-lc])]]])

(defn main-asset-view
  [{:keys [asset-type] :as signals}]
  (let [{:keys [subview]} (events/asset-view-info signals)
        asset-type @asset-type
        asset-type-name (loc/tr asset-type)
        type-lc (->> asset-type-name
                     string/lower-case)]
    [:div
     {:class "container my-4"}
     [:div
      {:class "flex justify-center"}
      [components/state-indicator signals]]
     (case subview
       :no-asset-info
       [no-asset-info-view type-lc]
       :processing [components/processing signals]
       :creator [create-asset-view signals]
       :summary [asset-resolved-view signals]
       :updater [update-asset-view signals]
       [:div ""])]))

(defn view
  [drag-state {:keys [] :as signals}]
  (let [;;TODO fix top-bottom draggability
        top-bottom? false
        #_(and (number? window-width)
               (< window-width 834))
        {:keys [show-info?]} (events/asset-view-info signals)]
    (if show-info?
      [(if top-bottom?
         drag-layout/top-bottom
         drag-layout/left-right)
       drag-state
       "webentry-resolution-layout"
       [info-bar signals]
       [main-asset-view signals]]
      [main-asset-view signals])))
